import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { AddItem } from '../../types/actions';
import { futureContentTypes } from '../../constants/options';
import TextAreaField from '../ui/TextAreaField';
import { api } from '../../api/client';
import { parseFormDraftResponse } from './aiFormDraftParser';
import AiDraftAssistPanel from './AiDraftAssistPanel';

export default function FutureContentForm({
  addItem,
  submitRef,
  showSubmit = true,
}: {
  addItem: AddItem;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  showSubmit?: boolean;
}) {
  const [content, setContent] = useState({ title: '', type: 'Encounter', description: '', prepNotes: '' });
  const [errors, setErrors] = useState<{ title?: string }>({});
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState('');
  const [isDraftingWithAi, setIsDraftingWithAi] = useState(false);

  const normalizeType = (value: string): string | undefined =>
    futureContentTypes.find((option) => option.toLowerCase() === value.trim().toLowerCase());

  const draftWithAi = useCallback(async () => {
    if (isDraftingWithAi) return;

    const prompt = aiPrompt.trim();
    const hasCurrentDraft = [content.title, content.description, content.prepNotes].some((value) => value.trim().length > 0);
    if (!prompt && !hasCurrentDraft) {
      setAiError('Add a prompt or some draft text before generating.');
      return;
    }

    const systemPrompt = `You draft future campaign content plans for tabletop GMs.
Return ONLY JSON with keys: title, type, description, prepNotes.
Rules:
- type must be one of: ${futureContentTypes.join(', ')}.
- keep output concise and prep-oriented.
- do not include extra keys or markdown fences.`;
    const userPrompt = `Create or refine this future content draft.
Current draft:
- title: ${content.title || '(empty)'}
- type: ${content.type}
- description: ${content.description || '(empty)'}
- prepNotes: ${content.prepNotes || '(empty)'}
GM prompt: ${prompt || '(none)'}`;

    setIsDraftingWithAi(true);
    setAiError('');
    try {
      const result = await api.sendAssistantMessage(
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        0.6
      );
      const parsed = parseFormDraftResponse(result.content, ['title', 'type', 'description', 'prepNotes']);
      if (!parsed) {
        throw new Error('AI returned an unexpected format. Try a more specific prompt.');
      }

      setContent((previous) => ({
        ...previous,
        title: parsed.title ?? previous.title,
        type: parsed.type ? (normalizeType(parsed.type) ?? previous.type) : previous.type,
        description: parsed.description ?? previous.description,
        prepNotes: parsed.prepNotes ?? previous.prepNotes,
      }));
    } catch (error) {
      setAiError(error instanceof Error ? error.message : 'Failed to generate future content draft with AI.');
    } finally {
      setIsDraftingWithAi(false);
    }
  }, [aiPrompt, content.description, content.prepNotes, content.title, content.type, isDraftingWithAi]);

  const handleSubmit = useCallback(() => {
    if (!content.title.trim()) {
      setErrors({ title: 'Title is required.' });
      return;
    }
    addItem('futureContent', content);
    setContent({ title: '', type: 'Encounter', description: '', prepNotes: '' });
    setAiPrompt('');
    setAiError('');
    setErrors({});
  }, [addItem, content]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
  }, [submitRef, handleSubmit]);

  return (
    <div className="bg-[var(--accent-muted)] p-4 rounded-lg border border-[var(--glass-border)] mb-4">
      <div className="field-block">
        <label className="field-label">Content Title</label>
        <input
          type="text"
          placeholder="Content Title"
          value={content.title}
          onChange={(e) => {
            setContent({ ...content, title: e.target.value });
            if (errors.title) setErrors({});
          }}
          className={`input-field ${errors.title ? 'input-error' : ''}`}
        />
        <div className="field-meta">
          <span className={errors.title ? 'field-error' : 'field-hint'}>
            {errors.title || 'Required.'}
          </span>
        </div>
      </div>
      <div className="field-block">
        <label className="field-label">Type</label>
        <select
          value={content.type}
          onChange={(e) => setContent({ ...content, type: e.target.value })}
          className="input-field"
        >
          {futureContentTypes.map((type) => (
            <option key={type}>{type}</option>
          ))}
        </select>
      </div>
      <TextAreaField
        label="Description"
        placeholder="Description"
        value={content.description}
        onChange={(value) => setContent({ ...content, description: value })}
        hint="What happens and why it matters."
        showCount
        minRows={4}
      />
      <TextAreaField
        label="Prep Notes"
        placeholder="Prep Notes"
        value={content.prepNotes}
        onChange={(value) => setContent({ ...content, prepNotes: value })}
        hint="NPCs, clues, and props to bring."
        showCount
        minRows={4}
      />
      <AiDraftAssistPanel
        value={aiPrompt}
        onChange={(value) => {
          setAiPrompt(value);
          if (aiError) setAiError('');
        }}
        onGenerate={() => void draftWithAi()}
        isGenerating={isDraftingWithAi}
        error={aiError}
        hint="Ask for scene framing, required prep assets, and fallback contingencies."
      />
      {showSubmit && (
        <button
          onClick={handleSubmit}
          className="btn-primary-solid flex items-center gap-2"
        >
          <Plus size={16} /> Add Future Content
        </button>
      )}
    </div>
  );
}
